// Phase 4 — Match Result Entry modal.
// Admin / captains use this to record per-player stats for a single
// cricket match. Each saved row goes into sports.MatchPerformance and
// auto-rolls into career stats on the leaderboard / player profile.
//
// The form pre-populates from the published squad if one exists, else
// from the team's full roster. Loading also pulls any previously saved
// rows so the admin can edit instead of duplicating.

import React, { useEffect, useMemo, useState } from "react";
import {
  X, Save, Loader2, Trophy, Hand, Target, Shield,
  Activity, Award, Zap, Sigma, ChevronDown, ChevronUp
} from "lucide-react";
import { useToast } from "./Toast";
import { api } from "../api";
import { canDo } from "../data";
import { fmtMatchTime } from "../utils/time";

// Empty stat row used as the form's starting shape for each player.
function emptyRow(userId, name) {
  return {
    userId, name,
    runsScored: 0,
    ballsFaced: 0,
    fours: 0,
    sixes: 0,
    isCentury: false,
    notOut: false,
    wicketsTaken: 0,
    oversBowled: 0,
    runsConceded: 0,
    maidenOvers: 0,
    catchesTaken: 0,
    stumpings: 0,
    notes: "",
    expanded: false
  };
}

export default function MatchResultEntry({ match, team, squad, members, user, onClose, onSaved }) {
  const toast = useToast();
  const canEdit = canDo(user, "matchPerformance", "update");

  // Pre-pick the roster: published-squad players if available, else the
  // team's full roster. Admins can record results even for unsquadded
  // matches (e.g. friendly games).
  const rosterIds = useMemo(() => {
    if (squad?.players?.length) return squad.players;
    return team?.players || [];
  }, [squad, team]);

  const rosterMembers = useMemo(() => rosterIds
    .map(id => members.find(m => m.id === id))
    .filter(Boolean), [rosterIds, members]);

  const [rows, setRows] = useState(() => rosterMembers.map(m => emptyRow(m.id, m.name)));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);

  // Load any previously-saved match performance rows so admins edit
  // rather than overwriting.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.listMatchPerformance(match.id)
      .then(saved => {
        if (cancelled) return;
        // Merge: roster member rows + persisted values where they exist.
        // Also include any saved player who isn't on the roster (rare
        // but possible if the squad shrank after the result was logged).
        const byId = new Map(saved.map(s => [s.userId, s]));
        const merged = [];
        const seen = new Set();
        for (const m of rosterMembers) {
          const s = byId.get(m.id);
          merged.push(s
            ? { ...emptyRow(m.id, m.name), ...s, expanded: false }
            : emptyRow(m.id, m.name));
          seen.add(m.id);
        }
        for (const s of saved) {
          if (!seen.has(s.userId)) {
            const mem = members.find(mm => mm.id === s.userId);
            merged.push({ ...emptyRow(s.userId, mem?.name || `Player #${s.userId}`), ...s, expanded: false });
          }
        }
        setRows(merged);
      })
      .catch(e => toast.error(e.message || "Couldn't load existing results."))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [match.id, rosterMembers.length]);

  // Close on Escape.
  useEffect(() => {
    const onKey = e => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  function patch(idx, field, value) {
    setRows(prev => {
      const next = [...prev];
      const row  = { ...next[idx], [field]: value };
      // Auto-check the century flag when runs cross 100. Admin can
      // uncheck later if needed (the explicit value sticks).
      if (field === "runsScored" && !row.centuryTouched) {
        row.isCentury = Number(value) >= 100;
      }
      if (field === "isCentury") row.centuryTouched = true;
      next[idx] = row;
      return next;
    });
  }

  function toggleExpand(idx) {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, expanded: !r.expanded } : r));
  }

  async function save() {
    if (!canEdit) return;
    setSaving(true);
    try {
      // Filter out rows that are entirely empty (no contribution).
      const payload = rows
        .filter(r => (
          r.runsScored || r.wicketsTaken || r.catchesTaken ||
          r.maidenOvers || r.fours || r.sixes || r.stumpings ||
          r.ballsFaced || r.oversBowled || r.runsConceded ||
          r.isCentury || r.notes
        ))
        .map(r => ({
          userId: r.userId,
          runsScored: r.runsScored,
          ballsFaced: r.ballsFaced,
          fours: r.fours,
          sixes: r.sixes,
          isCentury: r.isCentury,
          notOut: r.notOut,
          wicketsTaken: r.wicketsTaken,
          oversBowled: r.oversBowled,
          runsConceded: r.runsConceded,
          maidenOvers: r.maidenOvers,
          catchesTaken: r.catchesTaken,
          stumpings: r.stumpings,
          notes: r.notes
        }));
      await api.saveMatchPerformance(match.id, payload);
      toast.success(`Saved ${payload.length} player result${payload.length === 1 ? "" : "s"} for ${team?.name || "the team"}.`);
      onSaved && onSaved();
      onClose && onClose();
    } catch (e) {
      toast.error(e.message || "Save failed.");
    } finally { setSaving(false); }
  }

  // Quick totals so the admin can sanity-check before saving.
  const totals = useMemo(() => rows.reduce((acc, r) => ({
    runs:    acc.runs    + (+r.runsScored    || 0),
    wickets: acc.wickets + (+r.wicketsTaken  || 0),
    catches: acc.catches + (+r.catchesTaken  || 0),
    fours:   acc.fours   + (+r.fours         || 0),
    sixes:   acc.sixes   + (+r.sixes         || 0),
    tons:    acc.tons    + (r.isCentury ? 1 : 0)
  }), { runs: 0, wickets: 0, catches: 0, fours: 0, sixes: 0, tons: 0 }), [rows]);

  return (
    <div className="modal" onClick={onClose}>
      <div className="modalInner mre" onClick={e => e.stopPropagation()}>
        <button className="close" onClick={onClose} title="Close"><X size={20}/></button>

        <header className="mre-head">
          <div>
            <small>MATCH RESULT · {team?.name?.toUpperCase()}</small>
            <h2>vs {match.opponent}</h2>
            <em>{match.date} · {match.time ? `${fmtMatchTime(match.time)} ET` : "TBD"} · {match.venue}</em>
          </div>
          <div className="mre-totals">
            <div><Target size={14}/><b>{totals.runs}</b><span>runs</span></div>
            <div><Shield size={14}/><b>{totals.wickets}</b><span>wkts</span></div>
            <div><Hand size={14}/><b>{totals.catches}</b><span>catches</span></div>
            <div><Sigma size={14}/><b>{totals.fours}</b><span>4's</span></div>
            <div><Zap size={14}/><b>{totals.sixes}</b><span>6's</span></div>
            <div><Award size={14}/><b>{totals.tons}</b><span>100's</span></div>
          </div>
        </header>

        {loading ? (
          <div className="mre-loading">
            <Loader2 size={18} className="spin"/> Loading existing entries…
          </div>
        ) : (
          <>
            <div className="mre-rows">
              {rows.length === 0 && (
                <p style={{color:"var(--muted)", padding:24, textAlign:"center"}}>
                  No roster yet. Pick a squad or add players to this team first.
                </p>
              )}
              {rows.map((r, i) => (
                <div className="mre-row" key={r.userId}>
                  <div className="mre-row-head">
                    <div className="mre-row-name">
                      <Activity size={14}/>
                      <strong>{r.name}</strong>
                    </div>
                    <div className="mre-row-quick">
                      <Field
                        label="Runs" value={r.runsScored} accent="#5ce1e6"
                        onChange={v => patch(i, "runsScored", v)} disabled={!canEdit}
                      />
                      <Field
                        label="4's" value={r.fours} accent="#c897ff"
                        onChange={v => patch(i, "fours", v)} disabled={!canEdit}
                      />
                      <Field
                        label="6's" value={r.sixes} accent="#5ce1e6"
                        onChange={v => patch(i, "sixes", v)} disabled={!canEdit}
                      />
                      <Field
                        label="Wkts" value={r.wicketsTaken} accent="#ff8a2a"
                        onChange={v => patch(i, "wicketsTaken", v)} disabled={!canEdit}
                      />
                      <Field
                        label="Catch" value={r.catchesTaken} accent="#c897ff"
                        onChange={v => patch(i, "catchesTaken", v)} disabled={!canEdit}
                      />
                      <label className="mre-toggle" title="100+ runs flag">
                        <input
                          type="checkbox"
                          checked={!!r.isCentury}
                          disabled={!canEdit}
                          onChange={e => patch(i, "isCentury", e.target.checked)}
                        />
                        <Award size={13}/> 100
                      </label>
                      <button
                        type="button"
                        className="dark mre-row-more"
                        onClick={() => toggleExpand(i)}
                        title={r.expanded ? "Hide extra fields" : "Show extra fields"}
                      >
                        {r.expanded ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
                      </button>
                    </div>
                  </div>
                  {r.expanded && (
                    <div className="mre-row-extra">
                      <Field
                        label="Balls Faced" value={r.ballsFaced}
                        onChange={v => patch(i, "ballsFaced", v)} disabled={!canEdit}
                      />
                      <Field
                        label="Overs" value={r.oversBowled} step="0.1" decimal
                        onChange={v => patch(i, "oversBowled", v)} disabled={!canEdit}
                      />
                      <Field
                        label="Runs Conceded" value={r.runsConceded}
                        onChange={v => patch(i, "runsConceded", v)} disabled={!canEdit}
                      />
                      <Field
                        label="Maidens" value={r.maidenOvers}
                        onChange={v => patch(i, "maidenOvers", v)} disabled={!canEdit}
                      />
                      <Field
                        label="Stumpings" value={r.stumpings}
                        onChange={v => patch(i, "stumpings", v)} disabled={!canEdit}
                      />
                      <label className="mre-toggle">
                        <input
                          type="checkbox"
                          checked={!!r.notOut}
                          disabled={!canEdit}
                          onChange={e => patch(i, "notOut", e.target.checked)}
                        />
                        Not out
                      </label>
                      <div className="mre-notes">
                        <small>Notes</small>
                        <input
                          type="text"
                          value={r.notes || ""}
                          onChange={e => patch(i, "notes", e.target.value)}
                          placeholder="e.g. dropped two catches"
                          disabled={!canEdit}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {canEdit ? (
              <div className="mre-actions">
                <button className="dark" onClick={onClose} disabled={saving}>Cancel</button>
                <button className="btn" onClick={save} disabled={saving || rows.length === 0}>
                  {saving
                    ? <><Loader2 size={14} className="spin"/> Saving…</>
                    : <><Save size={14}/> Save Match Result</>}
                </button>
              </div>
            ) : (
              <p style={{color:"var(--muted)", textAlign:"center", padding:"8px 0"}}>
                Read-only view. Only admins and captains can record match results.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Small reusable numeric input with a tag label above.
function Field({ label, value, onChange, accent, disabled, decimal, step }) {
  return (
    <label className="mre-field" style={accent ? { "--accent": accent } : null}>
      <small>{label}</small>
      <input
        type="number"
        min="0"
        step={step || (decimal ? "0.1" : "1")}
        value={value}
        onChange={e => onChange(decimal ? Number(e.target.value) : Math.max(0, Math.floor(Number(e.target.value) || 0)))}
        disabled={disabled}
      />
    </label>
  );
}
